import type { Editor } from "@tiptap/core";
import {
  getAvailableDirectives,
  getDirectiveConfig,
  type DirectiveChoiceSlot,
  type DirectiveConfigItem,
} from "../../core/config";
import { buildDirectiveRaw } from "../../core/parseRaw";
import type { PickerListItem } from "@/features/sparxsEditor/components/molecules/pickerList/pickerList";
import {
  filterVariablesByDirective,
  filterVariablesPickerItemsByQuery,
} from "./variables";
import {
  isPrintTable,
  getPrintTableChoiceSlot,
  handlePrintTableChoiceSubmit,
  getTableVariableColumns,
} from "./printTable";
import type {
  DirectiveBuilderState,
  DirectiveSuggestionStateRef,
  InsertAndCloseFn,
} from "./printTable";
import type { Variables } from "@/lib/sparxs/metadataSchemas";
import { syncFloatingBuilderChoiceStorage } from "../storage/floatingStorage";
import { blurUpdateAndFocusFloatingDirectiveChoice } from "../../builder/interaction/focusChoice";

const directiveToItem = (key: string, label: string): PickerListItem => ({
  raw: `@${key}/`,
  label,
});

const getDirectiveItems = (variables: Variables[]): PickerListItem[] => {
  const directives = getAvailableDirectives(variables);
  return directives.map((d) => directiveToItem(d.key, d.label));
};

export const filterDirectiveItems = (
  variables: Variables[],
  query: string,
): PickerListItem[] => {
  const list = getDirectiveItems(variables);
  const trimmed = query.trim();
  if (!trimmed) return list;
  const q = trimmed.toLowerCase();
  return list.filter((item) => {
    const keyPart = item.raw.replace(/^@|\/$/g, "").toLowerCase();
    return (
      item.label.toLowerCase().includes(q) ||
      keyPart.includes(q) ||
      item.raw.toLowerCase().includes(q)
    );
  });
};

export interface DirectiveStepProps {
  items: PickerListItem[];
  stepTitle: string | undefined;
  choiceSlot: DirectiveChoiceSlot | undefined;
  choiceInitialValue: string;
  onSelectItem: (item: PickerListItem) => void;
  onChoiceSubmit: (value: string) => void;
}

export interface CurrentPropsLike {
  items: PickerListItem[];
  command: (item: PickerListItem) => void;
  clientRect: (() => DOMRect | null) | null;
}

type DirectiveBuilderViewSlice = Pick<
  DirectiveStepProps,
  "items" | "stepTitle" | "choiceSlot" | "choiceInitialValue"
>;

const computeDirectiveBuilderViewSlice = (
  b: DirectiveBuilderState,
  stateRef: DirectiveSuggestionStateRef,
  variables: Variables[],
  config: DirectiveConfigItem | undefined,
): DirectiveBuilderViewSlice => {
  const tableColumns =
    isPrintTable(b.directiveKey) && b.variableName !== ""
      ? getTableVariableColumns(variables, b.variableName)
      : undefined;

  if (b.step === "directive") {
    return {
      items: filterDirectiveItems(variables, stateRef.listFilterText),
      stepTitle: undefined,
      choiceSlot: undefined,
      choiceInitialValue: "",
    };
  }
  if (b.step === "variable") {
    const variablesForPicker =
      config != null
        ? filterVariablesByDirective(variables, config.variableTypes)
        : [];
    return {
      items: filterVariablesPickerItemsByQuery(
        variablesForPicker,
        stateRef.listFilterText,
      ),
      stepTitle: `Select variable for @${config?.label ?? b.directiveKey}`,
      choiceSlot: undefined,
      choiceInitialValue: "",
    };
  }
  let choiceSlot: DirectiveChoiceSlot | undefined;
  if (isPrintTable(b.directiveKey) && tableColumns != null) {
    choiceSlot = getPrintTableChoiceSlot(
      tableColumns,
      b.choices,
      b.choiceIndex,
    );
  } else {
    choiceSlot = config?.choiceSlots[b.choiceIndex];
  }
  return {
    items: [],
    stepTitle: `@${config?.label ?? b.directiveKey} / ${b.variableName}`,
    choiceSlot,
    choiceInitialValue: b.choices[b.choiceIndex] ?? "",
  };
};

export const getDirectiveBuilderStepProps = (
  editor: Editor,
  stateRef: DirectiveSuggestionStateRef,
  variables: Variables[],
  currentProps: CurrentPropsLike | null,
  insertAndClose: InsertAndCloseFn,
): DirectiveStepProps => {
  const b = stateRef.builder;
  const config =
    b.directiveKey !== "" ? getDirectiveConfig(b.directiveKey) : undefined;

  const { items, stepTitle, choiceSlot, choiceInitialValue } =
    computeDirectiveBuilderViewSlice(b, stateRef, variables, config);

  const onSelectItem = (item: PickerListItem) => {
    const { builder } = stateRef;
    if (builder.step === "directive") {
      currentProps?.command(item);
      const key = item.raw.replace(/^@|\/$/g, "");
      stateRef.listFilterText = "";
      stateRef.builder = {
        step: "variable",
        directiveKey: key,
        variableName: "",
        choices: [],
        choiceIndex: 0,
        selectedIndex: 0,
      };
      stateRef.update();
      return;
    }
    if (builder.step === "variable") {
      const varName = item.raw;
      const cols = isPrintTable(builder.directiveKey)
        ? getTableVariableColumns(variables, varName)
        : undefined;
      const configForDirective = getDirectiveConfig(builder.directiveKey);
      if (cols != null && cols.length > 0) {
        const dirKey = builder.directiveKey;
        stateRef.builder = {
          ...builder,
          step: "choice",
          variableName: varName,
          choiceIndex: 0,
        };
        syncFloatingBuilderChoiceStorage(editor, stateRef);
        stateRef.syncDocumentDirectiveRaw(
          buildDirectiveRaw(dirKey, varName, []),
        );
        blurUpdateAndFocusFloatingDirectiveChoice(stateRef);
        return;
      }
      if (configForDirective?.choiceSlots.length === 0) {
        insertAndClose(buildDirectiveRaw(builder.directiveKey, varName, []));
        return;
      }
      const dirKeyGeneric = builder.directiveKey;
      stateRef.builder = {
        ...builder,
        step: "choice",
        variableName: varName,
        choiceIndex: 0,
      };
      syncFloatingBuilderChoiceStorage(editor, stateRef);
      stateRef.syncDocumentDirectiveRaw(
        buildDirectiveRaw(dirKeyGeneric, varName, []),
      );
      blurUpdateAndFocusFloatingDirectiveChoice(stateRef);
    }
  };

  const onChoiceSubmit = (value: string) => {
    const { builder } = stateRef;
    const configAtSubmit =
      builder.directiveKey !== ""
        ? getDirectiveConfig(builder.directiveKey)
        : undefined;
    const tableColumnsAtSubmit =
      isPrintTable(builder.directiveKey) && builder.variableName !== ""
        ? getTableVariableColumns(variables, builder.variableName)
        : undefined;

    if (isPrintTable(builder.directiveKey) && tableColumnsAtSubmit != null) {
      handlePrintTableChoiceSubmit(
        editor,
        builder,
        value,
        stateRef,
        tableColumnsAtSubmit,
        insertAndClose,
      );
      return;
    }
    const slot = configAtSubmit?.choiceSlots[builder.choiceIndex];
    const trimmedChoice = value.trim();
    if (
      slot?.options != null &&
      trimmedChoice !== "" &&
      !slot.options.some((o) => o.value === trimmedChoice)
    ) {
      return;
    }
    const isOptionalSkip = slot?.options != null && trimmedChoice === "";
    const nextConfig = getDirectiveConfig(builder.directiveKey);
    if (isOptionalSkip && nextConfig != null) {
      const nextIndex = builder.choiceIndex + 1;
      if (nextIndex >= nextConfig.choiceSlots.length) {
        insertAndClose(
          buildDirectiveRaw(
            builder.directiveKey,
            builder.variableName,
            builder.choices,
          ),
        );
        return;
      }
      stateRef.builder = { ...builder, choiceIndex: nextIndex };
      syncFloatingBuilderChoiceStorage(editor, stateRef);
      stateRef.syncDocumentDirectiveRaw(
        buildDirectiveRaw(
          builder.directiveKey,
          builder.variableName,
          builder.choices,
        ),
      );
      blurUpdateAndFocusFloatingDirectiveChoice(stateRef);
      return;
    }
    const newChoices =
      trimmedChoice === ""
        ? builder.choices
        : (() => {
            const next = [...builder.choices];
            next[builder.choiceIndex] = trimmedChoice;
            return next;
          })();
    const maxChoices = nextConfig?.choiceSlots.length ?? 0;
    if (nextConfig != null && newChoices.length >= maxChoices) {
      insertAndClose(
        buildDirectiveRaw(
          builder.directiveKey,
          builder.variableName,
          newChoices,
        ),
      );
      return;
    }
    stateRef.builder = {
      ...builder,
      choices: newChoices,
      choiceIndex: newChoices.length,
    };
    syncFloatingBuilderChoiceStorage(editor, stateRef);
    stateRef.syncDocumentDirectiveRaw(
      buildDirectiveRaw(builder.directiveKey, builder.variableName, newChoices),
    );
    blurUpdateAndFocusFloatingDirectiveChoice(stateRef);
  };

  return {
    items,
    stepTitle,
    choiceSlot,
    choiceInitialValue,
    onSelectItem,
    onChoiceSubmit,
  };
};

export const getItemsForKeyDown = (
  builder: DirectiveBuilderState,
  variables: Variables[],
  _currentProps: CurrentPropsLike | null,
  listFilterText: string,
): PickerListItem[] => {
  if (builder.step === "directive") {
    return filterDirectiveItems(variables, listFilterText);
  }
  if (builder.step === "variable") {
    const config = getDirectiveConfig(builder.directiveKey);
    if (config == null) return [];
    return filterVariablesPickerItemsByQuery(
      filterVariablesByDirective(variables, config.variableTypes),
      listFilterText,
    );
  }
  return [];
};
